import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';

import { AuthCard } from '../components/common/AuthCard';
import { FormAlert, SubmitButton, TextField, vOtp } from '../components/form';
import { failCountOf, get, post } from '../lib/api';
import config from '../lib/config';
import { loginUser, useAuthDispatch, useAuthState } from '../auth/AuthContext';
import type { LocalData } from '../auth/types';
import { getLoginEndpoints } from '../auth/endpoints';
import { Roles } from '../auth/roles';
import { Paths } from '../routes/paths';
import { usePageTitle } from '../lib/usePageTitle';

type Method = 'authy' | 'email';

const CAPTCHA_LIMIT = 5;
const KEY_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function makeKey(length = 8): string {
    let result = '';
    for (let i = 0; i < length; i++) {
        result += KEY_CHARS.charAt(Math.floor(Math.random() * KEY_CHARS.length));
    }
    return result;
}

const otpSchema = z.object({ optCode: vOtp });
type OtpValues = z.infer<typeof otpSchema>;

export function AuthOptionsPage() {
    const { t } = useTranslation();
    const dispatch = useAuthDispatch();
    const auth = useAuthState();
    usePageTitle(t('twofa.title'));

    // The 2FA continue/verify actions always go through the "login" controller
    // — for AD users too. (The legacy app appeared to pass the AD endpoints
    // along, but AuthOptions never actually read them, so production has
    // always 2FA'd against login/*; identity comes from the temp token.)
    const endpoints = getLoginEndpoints('login');

    // Email OTP only works for local users — their Identity record carries an
    // emailable address. AD users authenticate via LDAP and have no usable
    // email in this flow (the code is never created), so we offer them Authy
    // only. AD temp identities carry the Admin/Internal temp roles; local ones
    // carry TemporaryUser.
    const isAd =
        auth.roles.includes(Roles.TemporaryAdminUser) ||
        auth.roles.includes(Roles.TemporaryInternalUser);

    const [method, setMethod] = useState<Method>('authy');
    const [isTrusted, setIsTrusted] = useState(false);
    const [appKey, setAppKey] = useState('');
    const [copied, setCopied] = useState(false);
    const [step, setStep] = useState<'choose' | 'code'>('choose');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [failCount, setFailCount] = useState(0);
    const [captcha, setCaptcha] = useState('');
    const [done, setDone] = useState(false);
    const captchaRef = useRef<ReCAPTCHA | null>(null);

    const otpMethods = useForm<OtpValues>({
        resolver: zodResolver(otpSchema),
        defaultValues: { optCode: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        get<number>('login/LoginEmailErrorCount')
            .then((r) => setFailCount(r.data ?? 0))
            .catch(() => {
                /* best-effort */
            });
        if (auth.roles.includes(Roles.TemporaryUser)) setIsTrusted(true);
    }, [auth.roles]);

    function resetCaptcha() {
        try {
            captchaRef.current?.reset();
        } catch {
            /* widget only mounts past the limit */
        }
        setCaptcha('');
    }

    async function startAuthy() {
        setError('');
        const key = makeKey();
        setAppKey(key);
        setCopied(false);
        setLoading(true);
        try {
            const res = await post<LocalData>(endpoints.authy, {
                AlphaNumericString: key,
                IsTrusted: isTrusted,
            });
            if (res.status === 200 && res.data) {
                loginUser(dispatch, res.data);
                setDone(true);
            } else {
                setError(t('twofa.error'));
            }
        } catch {
            setError(t('twofa.error'));
        } finally {
            setLoading(false);
        }
    }

    async function startEmail() {
        setError('');
        setAppKey('');
        setLoading(true);
        try {
            const res = await get(endpoints.email);
            if (res.status === 200) {
                setStep('code');
            } else {
                setError(t('twofa.error'));
            }
        } catch {
            setError(t('twofa.error'));
        } finally {
            setLoading(false);
        }
    }

    function onChoose(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (method === 'authy') void startAuthy();
        else void startEmail();
    }

    async function onVerify(values: OtpValues) {
        setError('');
        setLoading(true);
        try {
            const res = await post<LocalData>(endpoints.optCode, { optCode: values.optCode });
            if (res.status === 200 && res.data) {
                loginUser(dispatch, res.data);
                setDone(true);
            } else {
                setError(t('twofa.error'));
                resetCaptcha();
            }
        } catch (err) {
            const fc = failCountOf(err);
            if (typeof fc === 'number') setFailCount(fc);
            setError(t('twofa.error'));
            resetCaptcha();
        } finally {
            setLoading(false);
        }
    }

    async function resendEmail() {
        // Legacy bug: the "resend" link re-verified the code. Here it truly
        // re-requests a fresh email.
        otpMethods.reset({ optCode: '' });
        await startEmail();
    }

    function copyKey() {
        navigator.clipboard
            ?.writeText(appKey)
            .then(() => setCopied(true))
            .catch(() => {
                /* clipboard may be unavailable */
            });
    }

    if (done) return <Navigate to={Paths.home} replace />;

    const captchaRequired = failCount > CAPTCHA_LIMIT;
    const blockedByCaptcha = captchaRequired && captcha.length === 0;
    const attemptsLeft = CAPTCHA_LIMIT + 1 - failCount;

    return (
        <AuthCard title={t('twofa.title')} subtitle={t('twofa.subtitle')}>
            <FormAlert onClose={() => setError('')}>{error || undefined}</FormAlert>

            {step === 'choose' ? (
                <Form noValidate onSubmit={onChoose}>
                    <fieldset className="mb-3" disabled={loading}>
                        <legend className="visually-hidden">{t('twofa.subtitle')}</legend>
                        <Form.Check
                            type="radio"
                            name="twofa-method"
                            id="twofa-authy"
                            label={t('twofa.authy')}
                            checked={method === 'authy'}
                            onChange={() => setMethod('authy')}
                        />
                        {!isAd && (
                            <Form.Check
                                type="radio"
                                name="twofa-method"
                                id="twofa-email"
                                className="mt-1"
                                label={t('twofa.emailNotRecommended')}
                                checked={method === 'email'}
                                onChange={() => setMethod('email')}
                            />
                        )}
                    </fieldset>

                    {appKey && (
                        <Card body className="mb-3 bg-body-tertiary border-0">
                            <div className="small text-body-secondary mb-1">{t('twofa.appKey')}</div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span
                                    className="fs-4 fw-medium font-monospace"
                                    style={{ letterSpacing: '0.15em' }}
                                >
                                    {appKey}
                                </span>
                                <Button size="sm" variant="outline-secondary" type="button" onClick={copyKey}>
                                    {copied ? t('common.copied') : t('twofa.copyKey')}
                                </Button>
                            </div>
                            <div className="small text-body-secondary mt-2 d-flex align-items-center gap-2">
                                {loading && <Spinner as="span" size="sm" animation="border" aria-hidden="true" />}
                                {t('twofa.authyHint')}
                            </div>
                        </Card>
                    )}

                    <Form.Check
                        type="checkbox"
                        id="twofa-remember"
                        className="mb-1"
                        label={t('twofa.rememberDevice')}
                        checked={isTrusted}
                        onChange={(e) => setIsTrusted(e.target.checked)}
                        disabled={loading}
                    />
                    <Form.Text className="text-muted d-block mb-3">{t('twofa.rememberDeviceHint')}</Form.Text>

                    <SubmitButton loading={loading}>{t('common.login')}</SubmitButton>
                </Form>
            ) : (
                <FormProvider {...otpMethods}>
                    <p className="text-body-secondary">{t('twofa.emailSent')}</p>
                    <Form noValidate onSubmit={otpMethods.handleSubmit(onVerify)}>
                        <TextField
                            name="optCode"
                            label={t('twofa.code')}
                            type="text"
                            inputMode="numeric"
                            placeholder={t('twofa.codePlaceholder')}
                            autoComplete="one-time-code"
                            required
                        />

                        {failCount >= 3 && failCount <= CAPTCHA_LIMIT && (
                            <p className="small text-warning-emphasis">
                                {t('twofa.attemptsLeft', { count: Math.max(0, attemptsLeft) })}
                            </p>
                        )}

                        {captchaRequired && (
                            <div className="mb-3 captcha-wrap">
                                <ReCAPTCHA
                                    ref={captchaRef}
                                    sitekey={config.recaptchaSiteKey}
                                    onChange={(v) => setCaptcha(v ?? '')}
                                />
                                <Form.Text className="text-muted d-block mt-1">
                                    {t('login.captchaHint')}
                                </Form.Text>
                            </div>
                        )}

                        <div className="d-flex align-items-center gap-3">
                            <SubmitButton loading={loading} disabled={blockedByCaptcha}>
                                {t('twofa.verify')}
                            </SubmitButton>
                            <Button
                                variant="link"
                                type="button"
                                className="p-0"
                                onClick={() => void resendEmail()}
                                disabled={loading}
                            >
                                {t('twofa.resendEmail')}
                            </Button>
                        </div>
                    </Form>
                </FormProvider>
            )}
        </AuthCard>
    );
}
